import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Box, Typography, TextField, Paper, CircularProgress, Alert,
    Select, MenuItem, Checkbox, ListItemText, InputAdornment,
    FormControl, InputLabel, Chip, useTheme, Tooltip, IconButton,
    OutlinedInput, SelectChangeEvent, Button,
    Dialog, DialogTitle, DialogContent, DialogActions, useMediaQuery,
} from '@mui/material';
import { keyframes } from '@mui/system';
import SearchIcon from '@mui/icons-material/Search';
import SortOutlined from '@mui/icons-material/SortOutlined';
import FilterListOff from '@mui/icons-material/FilterListOff';
import FilterList from '@mui/icons-material/FilterList';
import TuneOutlined from '@mui/icons-material/TuneOutlined';
import CloseIcon from '@mui/icons-material/Close';
import {
    PlayArrow, Pause, ChevronLeft, ChevronRight,
    AccessTime, Sync, ZoomOut, ZoomIn,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';

import { DynamicBreadcrumbs } from 'components/Breadcrumb';
import apiClient from 'utils/ApiClient';
import { RootState } from 'state/store';
import { AUDIT_EVENT_TYPES, getEventTypeLabel } from './auditEventTypes';
import {
    AuditTimePickerPopover, AUDIT_QUICK_RANGES, fmtDate,
    type AuditQuickRange,
} from './AuditTimePickerPopover';

const PAGE_SIZE = 30;
const LIVE_POLL_MS = 10_000;
const TOGGLE_ALL_SENTINEL = '__TOGGLE_ALL__';

const liveBlinkAnimation = keyframes`
  0%, 100% { box-shadow: 0 0 4px rgba(76, 175, 80, 0.6); }
  50% { box-shadow: 0 0 14px rgba(76, 175, 80, 1), 0 0 28px rgba(76, 175, 80, 0.5); }
`;

interface AuditLogEntry {
    _id: string;
    eventType: string;
    eventDetails: string;
    loggedUser: string;
    createdAt: string;
}

type SortOrder = 'DESC' | 'ASC';

const EVENT_TYPE_COLOR_MAP: Record<string, string> = {
    sys: '#5c6bc0',
    iu: '#26a69a',
    platform: '#ef5350',
};

function getEventColor(eventType: string): string {
    const prefix = eventType.split('-')[0];
    return EVENT_TYPE_COLOR_MAP[prefix] ?? '#78909c';
}

function formatTimestamp(iso: string): { date: string; time: string } {
    const d = new Date(iso);
    return {
        date: d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }),
        time: d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
    };
}

function toLocalDatetimeStr(date: Date) {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export const AuditLogs: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const user = useSelector((state: RootState) => state.authentication.user);
    const companyId = user?.companyId;

    // --- Mobile filter dialog ---
    const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

    // --- Data state ---
    const [logs, setLogs] = useState<AuditLogEntry[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // --- Filter state ---
    const [searchText, setSearchText] = useState('');
    const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>([...AUDIT_EVENT_TYPES]);
    const [sortOrder, setSortOrder] = useState<SortOrder>('DESC');
    const allSelected = selectedEventTypes.length === AUDIT_EVENT_TYPES.length;

    // --- Time range state ---
    const [timeRangeLabel, setTimeRangeLabel] = useState('Last 24 hours');
    const [activeRangeMs, setActiveRangeMs] = useState(24 * 60 * 60 * 1000);
    const [absoluteFrom, setAbsoluteFrom] = useState(Date.now() - 24 * 60 * 60 * 1000);
    const [absoluteTo, setAbsoluteTo] = useState(Date.now());
    const [isRelative, setIsRelative] = useState(true);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    // --- LIVE state ---
    const [isLive, setIsLive] = useState(false);
    const liveIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const getTimeRangeMs = useCallback((): { start: number; end: number } => {
        if (isRelative) {
            const now = Date.now();
            return { start: now - activeRangeMs, end: now };
        }
        return { start: absoluteFrom, end: absoluteTo };
    }, [isRelative, activeRangeMs, absoluteFrom, absoluteTo]);

    // --- API calls ---
    const buildUrl = useCallback((pageNum: number) => {
        const { start, end } = getTimeRangeMs();
        return `/audit/${companyId}/logs?page=${pageNum}&limit=${PAGE_SIZE}&start=${start}&end=${end}`;
    }, [companyId, getTimeRangeMs]);

    const fetchPage = useCallback(async (pageNum: number, replace: boolean) => {
        if (!companyId) return;
        try {
            if (replace) setLoading(true); else setLoadingMore(true);
            setError(null);
            const response = await apiClient.get(buildUrl(pageNum));
            const data = response.data ?? response;
            const fetched: AuditLogEntry[] = data.logs ?? [];
            const serverTotal: number = data.total ?? 0;

            if (replace) {
                setLogs(fetched);
            } else {
                setLogs(prev => {
                    const existingIds = new Set(prev.map(l => l._id));
                    const deduped = fetched.filter(l => !existingIds.has(l._id));
                    return [...prev, ...deduped];
                });
            }
            setTotal(serverTotal);
            setPage(pageNum);
            setHasMore(pageNum * PAGE_SIZE < serverTotal);
        } catch (err: any) {
            setError(err?.message ?? 'Failed to fetch audit logs');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [companyId, buildUrl]);

    const handleRunQuery = useCallback(() => {
        setIsLive(false);
        fetchPage(1, true);
    }, [fetchPage]);

    const handleLoadMore = useCallback(() => {
        fetchPage(page + 1, false);
    }, [fetchPage, page]);

    // Initial fetch
    useEffect(() => {
        fetchPage(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId]);

    // --- LIVE polling ---
    const livePoll = useCallback(async () => {
        if (!companyId) return;
        try {
            const response = await apiClient.get(buildUrl(1));
            const data = response.data ?? response;
            const fetched: AuditLogEntry[] = data.logs ?? [];
            setLogs(prev => {
                const existingIds = new Set(prev.map(l => l._id));
                const newEntries = fetched.filter(l => !existingIds.has(l._id));
                if (newEntries.length === 0) return prev;
                return [...newEntries, ...prev];
            });
            setTotal(data.total ?? 0);
        } catch { /* silent during live */ }
    }, [companyId, buildUrl]);

    useEffect(() => {
        if (isLive) {
            liveIntervalRef.current = setInterval(livePoll, LIVE_POLL_MS);
            return () => { if (liveIntervalRef.current) clearInterval(liveIntervalRef.current); };
        }
        if (liveIntervalRef.current) { clearInterval(liveIntervalRef.current); liveIntervalRef.current = null; }
        return undefined;
    }, [isLive, livePoll]);

    const handleToggleLive = useCallback(() => {
        if (isLive) {
            setIsLive(false);
        } else {
            fetchPage(1, true);
            setIsLive(true);
        }
    }, [isLive, fetchPage]);

    // --- Time range handlers ---
    const handleQuickRangeSelect = useCallback((range: AuditQuickRange) => {
        setTimeRangeLabel(range.label);
        setActiveRangeMs(range.ms);
        setIsRelative(true);
        const now = Date.now();
        setAbsoluteFrom(now - range.ms);
        setAbsoluteTo(now);
        setAnchorEl(null);
    }, []);

    const handleApplyCustomRange = useCallback((fromMs: number, toMs: number, label: string) => {
        setAbsoluteFrom(fromMs);
        setAbsoluteTo(toMs);
        setActiveRangeMs(toMs - fromMs);
        setIsRelative(false);
        setTimeRangeLabel(label);
        setAnchorEl(null);
    }, []);

    const shiftTime = useCallback((direction: 'back' | 'forward') => {
        const shiftMs = direction === 'back' ? -activeRangeMs : activeRangeMs;
        const now = Date.now();
        let currentFrom = isRelative ? now - activeRangeMs : absoluteFrom;
        let currentTo = isRelative ? now : absoluteTo;
        let newFrom = currentFrom + shiftMs;
        let newTo = currentTo + shiftMs;

        if (direction === 'forward' && newTo > now) {
            newTo = now; newFrom = now - activeRangeMs;
            const qr = AUDIT_QUICK_RANGES.find(r => r.ms === activeRangeMs);
            if (qr) { setTimeRangeLabel(qr.label); setIsRelative(true); }
            else { setTimeRangeLabel(`${fmtDate(new Date(newFrom))} to ${fmtDate(new Date(newTo))}`); setIsRelative(false); }
            setAbsoluteFrom(newFrom); setAbsoluteTo(newTo);
            return;
        }
        setAbsoluteFrom(newFrom); setAbsoluteTo(newTo);
        setIsRelative(false);
        setTimeRangeLabel(`${fmtDate(new Date(newFrom))} to ${fmtDate(new Date(newTo))}`);
    }, [activeRangeMs, isRelative, absoluteFrom, absoluteTo]);

    const handleZoomIn = useCallback(() => {
        const idx = AUDIT_QUICK_RANGES.findIndex(r => r.label === timeRangeLabel);
        if (idx > 0) handleQuickRangeSelect(AUDIT_QUICK_RANGES[idx - 1]);
        else if (idx === -1) {
            const closest = AUDIT_QUICK_RANGES.findIndex(r => r.ms >= activeRangeMs);
            handleQuickRangeSelect(AUDIT_QUICK_RANGES[Math.max(0, closest - 1)]);
        }
    }, [timeRangeLabel, activeRangeMs, handleQuickRangeSelect]);

    const handleZoomOut = useCallback(() => {
        const idx = AUDIT_QUICK_RANGES.findIndex(r => r.label === timeRangeLabel);
        if (idx >= 0 && idx < AUDIT_QUICK_RANGES.length - 1) handleQuickRangeSelect(AUDIT_QUICK_RANGES[idx + 1]);
    }, [timeRangeLabel, handleQuickRangeSelect]);

    const pickerDates = useMemo(() => {
        const now = new Date();
        const from = isRelative ? new Date(now.getTime() - activeRangeMs) : new Date(absoluteFrom);
        const to = isRelative ? now : new Date(absoluteTo);
        return { from: toLocalDatetimeStr(from), to: toLocalDatetimeStr(to) };
    }, [isRelative, activeRangeMs, absoluteFrom, absoluteTo]);

    const tzOffset = new Date().getTimezoneOffset();
    const tzSign = tzOffset <= 0 ? '+' : '-';
    const tzHours = String(Math.floor(Math.abs(tzOffset) / 60)).padStart(2, '0');
    const tzMins = String(Math.abs(tzOffset) % 60).padStart(2, '0');
    const tzName = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const tzString = `UTC${tzSign}${tzHours}:${tzMins}`;

    // --- Event type toggle ---
    const handleEventTypeChange = useCallback((event: SelectChangeEvent<string[]>) => {
        const raw = event.target.value as string[];
        if (raw.includes(TOGGLE_ALL_SENTINEL)) {
            setSelectedEventTypes(prev => prev.length === AUDIT_EVENT_TYPES.length ? [] : [...AUDIT_EVENT_TYPES]);
            return;
        }
        setSelectedEventTypes(raw);
    }, []);

    const handleToggleAll = useCallback(() => {
        setSelectedEventTypes(prev => prev.length === AUDIT_EVENT_TYPES.length ? [] : [...AUDIT_EVENT_TYPES]);
    }, []);

    // --- Filtered + sorted view ---
    const filteredAndSortedLogs = useMemo(() => {
        let result = logs;

        // Event type filter
        if (selectedEventTypes.length < AUDIT_EVENT_TYPES.length) {
            const set = new Set(selectedEventTypes);
            result = result.filter(log => set.has(log.eventType));
        }

        // Search filter
        const term = searchText.trim().toLowerCase();
        if (term) {
            result = result.filter(log =>
                log.eventType.toLowerCase().includes(term) ||
                getEventTypeLabel(log.eventType).toLowerCase().includes(term) ||
                (log.eventDetails ?? '').toLowerCase().includes(term) ||
                (log.loggedUser ?? '').toLowerCase().includes(term),
            );
        }

        const sorted = [...result];
        sorted.sort((a, b) => {
            const ta = new Date(a.createdAt).getTime();
            const tb = new Date(b.createdAt).getTime();
            return sortOrder === 'DESC' ? tb - ta : ta - tb;
        });
        return sorted;
    }, [logs, selectedEventTypes, searchText, sortOrder]);

    const breadcrumbItems = [
        { itemPath: '/dashboard', itemLabel: 'Home' },
        { itemPath: '', itemLabel: 'Audit Logs' },
    ];

    const isLightToolbar = theme.palette.mode === 'light';
    const isProfessionalLight = isLightToolbar && Boolean(theme.palette.dashboard?.heroTileForegroundColor);
    const timelineAccentColor = isProfessionalLight ? 'rgb(25, 25, 25)' : theme.palette.secondary.main;

    const secondaryLabelSx = isLightToolbar
        ? {
            color: 'rgb(25, 25, 25)',
            fontWeight: 600,
            '&.Mui-focused': { color: 'rgb(25, 25, 25)' },
            '&.MuiInputLabel-shrink': { color: 'rgb(25, 25, 25)' },
        }
        : {
            color: 'secondary.main',
            fontWeight: 600,
            '&.Mui-focused': { color: 'secondary.main' },
            '&.MuiInputLabel-shrink': { color: 'secondary.main' },
        };

    const secondaryCheckboxSx = {
        color: isLightToolbar ? 'rgb(25, 25, 25)' : 'secondary.main',
        '&.Mui-checked': { color: isLightToolbar ? 'rgb(25, 25, 25)' : 'secondary.main' },
        '&.MuiCheckbox-indeterminate': { color: isLightToolbar ? 'rgb(25, 25, 25)' : 'secondary.main' },
    };
    const toolbarAccentColor = isLightToolbar ? 'rgb(25, 25, 25)' : 'secondary.main';
    const toolbarAccentBorder = isLightToolbar ? 'rgba(25, 25, 25, 0.38)' : 'secondary.main';
    const timeRangeIconSx = {
        border: '1px solid',
        borderColor: toolbarAccentBorder,
        color: toolbarAccentColor,
        borderRadius: 1,
        ...(isLightToolbar && {
            '&.Mui-disabled': {
                borderColor: 'rgba(25, 25, 25, 0.26)',
                color: 'rgba(25, 25, 25, 0.38)',
            },
        }),
    };
    const timeRangePickerButtonSx = {
        textTransform: 'none' as const,
        fontWeight: 600,
        fontSize: '0.82rem',
        borderColor: toolbarAccentBorder,
        color: toolbarAccentColor,
        px: 1.5,
        whiteSpace: 'nowrap' as const,
    };

    const timeRangeControls = (pickerFlex?: boolean) => (
        <>
            <Tooltip title="Zoom in time range">
                <span>
                    <IconButton size="small" onClick={handleZoomIn}
                        disabled={timeRangeLabel === AUDIT_QUICK_RANGES[0].label}
                        sx={timeRangeIconSx}
                    >
                        <ZoomIn fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>

            <Tooltip title="Shift time range back">
                <IconButton size="small" onClick={() => shiftTime('back')} sx={timeRangeIconSx}>
                    <ChevronLeft fontSize="small" />
                </IconButton>
            </Tooltip>

            <Button
                size="small"
                variant="outlined"
                color={isLightToolbar ? undefined : 'secondary'}
                onClick={e => setAnchorEl(e.currentTarget)}
                startIcon={<AccessTime sx={{ fontSize: 16 }} />}
                sx={{ ...timeRangePickerButtonSx, ...(pickerFlex ? { flex: 1 } : {}) }}
            >
                {timeRangeLabel}
            </Button>

            <Tooltip title="Shift time range forward">
                <IconButton size="small" onClick={() => shiftTime('forward')} sx={timeRangeIconSx}>
                    <ChevronRight fontSize="small" />
                </IconButton>
            </Tooltip>

            <Tooltip title="Zoom out time range">
                <span>
                    <IconButton size="small" onClick={handleZoomOut}
                        disabled={timeRangeLabel === AUDIT_QUICK_RANGES[AUDIT_QUICK_RANGES.length - 1].label}
                        sx={timeRangeIconSx}
                    >
                        <ZoomOut fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
        </>
    );

    return (
        <Box sx={{ p: { xs: '12px', md: '20px' }, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
            <DynamicBreadcrumbs items={breadcrumbItems} />

            {/* ===== Toolbar ===== */}
            <Box sx={{
                display: 'flex', flexDirection: 'column', gap: 1.5, p: 1.5, mb: 2, borderRadius: 2,
                backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)',
            }}>
                {/* --- Mobile toolbar --- */}
                {isMobile && (
                    <>
                        {/* Row 1: Search only */}
                        <TextField
                            size="small" placeholder="Search audit logs..." value={searchText}
                            onChange={e => setSearchText(e.target.value)} fullWidth
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                        />

                        {/* Row 2: Run + Live + Burger */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Button variant="contained" color="secondary" size="small"
                                startIcon={<Sync sx={{ fontSize: 16 }} />}
                                onClick={handleRunQuery} disabled={loading}
                                sx={{ textTransform: 'none', fontWeight: 700, px: 1.5, whiteSpace: 'nowrap', minWidth: 'auto' }}
                            >
                                {loading ? '...' : 'Run'}
                            </Button>

                            <Button
                                variant={isLive ? 'contained' : 'outlined'}
                                color={isLive ? 'success' : 'secondary'} size="small"
                                startIcon={isLive ? <Pause sx={{ fontSize: 14 }} /> : <PlayArrow sx={{ fontSize: 14 }} />}
                                onClick={handleToggleLive}
                                sx={{
                                    textTransform: 'none', fontWeight: 600, px: 1, whiteSpace: 'nowrap', minWidth: 'auto',
                                    ...(isLive && {
                                        animation: `${liveBlinkAnimation} 1.5s ease-in-out infinite`,
                                        backgroundColor: 'success.main', color: '#fff',
                                        '&:hover': { backgroundColor: 'success.dark' },
                                    }),
                                }}
                            >
                                Live
                            </Button>

                            <Box sx={{ flex: 1 }} />

                            <IconButton size="small" onClick={() => setMobileFilterOpen(true)}
                                sx={{ border: '1px solid', borderColor: 'secondary.main', color: 'secondary.main', borderRadius: 1 }}
                            >
                                <TuneOutlined fontSize="small" />
                            </IconButton>
                        </Box>
                    </>
                )}

                {/* --- Desktop toolbar --- */}
                {!isMobile && (
                    <>
                        {/* Row 1: Search + Filters */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
                            <TextField
                                size="small" placeholder="Search audit logs..." value={searchText}
                                onChange={e => setSearchText(e.target.value)}
                                sx={{ minWidth: 200, flex: '1 1 200px' }}
                                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                            />

                            <FormControl size="small" sx={{ minWidth: 240, flex: '1 1 240px', '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'secondary.main' } }}>
                                <InputLabel id="audit-event-type-label" sx={secondaryLabelSx}>Event Types</InputLabel>
                                <Select
                                    labelId="audit-event-type-label" multiple value={selectedEventTypes}
                                    onChange={handleEventTypeChange}
                                    input={<OutlinedInput label="Event Types" />}
                                    renderValue={(sel) => {
                                        if (sel.length === AUDIT_EVENT_TYPES.length) return 'All Selected';
                                        if (sel.length === 0) return 'None Selected';
                                        return `${sel.length} selected`;
                                    }}
                                    MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}
                                >
                                    <MenuItem value={TOGGLE_ALL_SENTINEL} sx={{ fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider' }}>
                                        <Checkbox checked={allSelected} indeterminate={selectedEventTypes.length > 0 && !allSelected} sx={secondaryCheckboxSx} />
                                        <ListItemText primary={allSelected ? 'Deselect All' : 'Select All'} />
                                    </MenuItem>
                                    {AUDIT_EVENT_TYPES.map(et => (
                                        <MenuItem key={et} value={et}>
                                            <Checkbox checked={selectedEventTypes.includes(et)} sx={secondaryCheckboxSx} />
                                            <ListItemText primary={getEventTypeLabel(et)} primaryTypographyProps={{ variant: 'body2', noWrap: true }} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Tooltip title={allSelected ? 'Deselect all event types' : 'Select all event types'}>
                                <IconButton size="small" onClick={handleToggleAll} color={allSelected ? 'default' : 'primary'}>
                                    {allSelected ? <FilterListOff fontSize="small" /> : <FilterList fontSize="small" />}
                                </IconButton>
                            </Tooltip>

                            <FormControl size="small" sx={{ minWidth: 140, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'secondary.main' } }}>
                                <InputLabel id="audit-sort-label" sx={secondaryLabelSx}>Sort Order</InputLabel>
                                <Select
                                    labelId="audit-sort-label" value={sortOrder} label="Sort Order"
                                    onChange={e => setSortOrder(e.target.value as SortOrder)}
                                    startAdornment={<SortOutlined fontSize="small" sx={{ mr: 0.5 }} />}
                                >
                                    <MenuItem value="DESC">Newest First</MenuItem>
                                    <MenuItem value="ASC">Oldest First</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        {/* Row 2: Time range controls + Run Query + Live */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {timeRangeControls()}
                            </Box>

                            <Box sx={{ flex: 1 }} />

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Button variant="contained" color="secondary" size="small"
                                    startIcon={<Sync sx={{ fontSize: 16 }} />}
                                    onClick={handleRunQuery} disabled={loading}
                                    sx={{ textTransform: 'none', fontWeight: 700, px: 2, whiteSpace: 'nowrap' }}
                                >
                                    {loading ? 'Loading...' : 'Run query'}
                                </Button>

                                <Button
                                    variant={isLive ? 'contained' : 'outlined'}
                                    color={isLive ? 'success' : 'secondary'} size="small"
                                    startIcon={isLive ? <Pause sx={{ fontSize: 16 }} /> : <PlayArrow sx={{ fontSize: 16 }} />}
                                    onClick={handleToggleLive}
                                    sx={{
                                        textTransform: 'none', fontWeight: 600, px: 1.5, whiteSpace: 'nowrap',
                                        ...(isLive && {
                                            animation: `${liveBlinkAnimation} 1.5s ease-in-out infinite`,
                                            backgroundColor: 'success.main', color: '#fff',
                                            '&:hover': { backgroundColor: 'success.dark' },
                                        }),
                                    }}
                                >
                                    Live
                                </Button>
                            </Box>
                        </Box>
                    </>
                )}
            </Box>

            {/* ===== Mobile Filter Dialog ===== */}
            <Dialog open={mobileFilterOpen} onClose={() => setMobileFilterOpen(false)} fullWidth maxWidth="sm"
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: (t) => t.palette.heading?.main ?? t.palette.secondary.main }}>Filters</Typography>
                    <IconButton size="small" onClick={() => setMobileFilterOpen(false)}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '8px !important' }}>
                    {/* Event Types */}
                    <FormControl size="small" fullWidth sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'secondary.main' } }}>
                        <InputLabel id="audit-event-type-label-mobile" sx={secondaryLabelSx}>Event Types</InputLabel>
                        <Select
                            labelId="audit-event-type-label-mobile" multiple value={selectedEventTypes}
                            onChange={handleEventTypeChange}
                            input={<OutlinedInput label="Event Types" />}
                            renderValue={(sel) => {
                                if (sel.length === AUDIT_EVENT_TYPES.length) return 'All Selected';
                                if (sel.length === 0) return 'None Selected';
                                return `${sel.length} selected`;
                            }}
                            MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                        >
                            <MenuItem value={TOGGLE_ALL_SENTINEL} sx={{ fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Checkbox checked={allSelected} indeterminate={selectedEventTypes.length > 0 && !allSelected} sx={secondaryCheckboxSx} />
                                <ListItemText primary={allSelected ? 'Deselect All' : 'Select All'} />
                            </MenuItem>
                            {AUDIT_EVENT_TYPES.map(et => (
                                <MenuItem key={et} value={et}>
                                    <Checkbox checked={selectedEventTypes.includes(et)} sx={secondaryCheckboxSx} />
                                    <ListItemText primary={getEventTypeLabel(et)} primaryTypographyProps={{ variant: 'body2', noWrap: true }} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Sort Order */}
                    <FormControl size="small" fullWidth sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'secondary.main' } }}>
                        <InputLabel id="audit-sort-label-mobile" sx={secondaryLabelSx}>Sort Order</InputLabel>
                        <Select
                            labelId="audit-sort-label-mobile" value={sortOrder} label="Sort Order"
                            onChange={e => setSortOrder(e.target.value as SortOrder)}
                            startAdornment={<SortOutlined fontSize="small" sx={{ mr: 0.5 }} />}
                        >
                            <MenuItem value="DESC">Newest First</MenuItem>
                            <MenuItem value="ASC">Oldest First</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Time Range */}
                    <Box>
                        <Typography variant="caption" sx={{
                            fontWeight: 600,
                            mb: 1,
                            display: 'block',
                            color: isLightToolbar ? 'rgb(25, 25, 25)' : 'secondary.main',
                        }}
                        >
                            Time Range
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                            {timeRangeControls(true)}
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button variant="contained" color="secondary" fullWidth
                        onClick={() => { setMobileFilterOpen(false); handleRunQuery(); }}
                        startIcon={<Sync sx={{ fontSize: 16 }} />}
                        sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                        Apply &amp; Run Query
                    </Button>
                </DialogActions>
            </Dialog>

            <AuditTimePickerPopover
                anchorEl={anchorEl} onClose={() => setAnchorEl(null)}
                timeRangeLabel={timeRangeLabel}
                onQuickRangeSelect={handleQuickRangeSelect}
                onApplyCustomRange={handleApplyCustomRange}
                initialFrom={pickerDates.from} initialTo={pickerDates.to}
                tzName={tzName} tzString={tzString}
            />

            {/* Stats */}
            {!loading && !error && (
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                    Showing {filteredAndSortedLogs.length} of {logs.length} loaded ({total} total)
                    {isLive && <Chip label="LIVE" size="small" color="success" sx={{ ml: 1, height: 18, fontSize: '0.65rem', fontWeight: 700 }} />}
                </Typography>
            )}

            {/* Loading */}
            {loading && (
                <Paper sx={{ p: 6, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={36} color="secondary" />
                    <Typography variant="body1" color="text.secondary">Fetching audit logs...</Typography>
                </Paper>
            )}

            {/* Error */}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            {/* Empty state */}
            {!loading && !error && filteredAndSortedLogs.length === 0 && (
                <Paper sx={{ p: 6, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                        {logs.length === 0 ? 'No audit logs found. Click "Run query" to fetch logs.' : 'No logs match the current filters or time range.'}
                    </Typography>
                </Paper>
            )}

            {/* ===== Timeline ===== */}
            {!loading && filteredAndSortedLogs.length > 0 && (
                <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', pr: { xs: 0, md: 1 } }}>
                    <Box sx={{ position: 'relative', pl: '40px', pb: 4, minWidth: 0 }}>
                        <Box sx={{
                            position: 'absolute', left: '15px', top: 0, bottom: 0, width: '2px',
                            bgcolor: timelineAccentColor,
                            opacity: isProfessionalLight ? 0.45 : 0.35,
                        }} />

                        {filteredAndSortedLogs.map((log) => {
                            const chipColor = getEventColor(log.eventType);
                            const { date, time } = formatTimestamp(log.createdAt);
                            const secondaryMain = theme.palette.secondary.main;

                            return (
                                <Box key={log._id} sx={{ position: 'relative', mb: 2.5, display: 'flex', alignItems: 'flex-start', minWidth: 0 }}>
                                    <Box sx={{
                                        position: 'absolute', left: '-33px', top: '16px', width: 14, height: 14, borderRadius: '50%',
                                        bgcolor: timelineAccentColor,
                                        border: '2px solid', borderColor: 'background.paper',
                                        boxShadow: isProfessionalLight
                                            ? '0 0 0 3px rgba(25, 25, 25, 0.15)'
                                            : `0 0 0 3px ${secondaryMain}30`,
                                        zIndex: 1,
                                    }} />
                                    <Box sx={{
                                        position: 'absolute', left: '-19px', top: '22px', width: '19px', height: '2px',
                                        bgcolor: timelineAccentColor,
                                        opacity: isProfessionalLight ? 0.65 : 0.6,
                                    }} />

                                    <Paper elevation={0} sx={{
                                        flex: 1, minWidth: 0, p: { xs: 1.5, md: 2 }, border: '1px solid', borderColor: `${secondaryMain}40`, borderRadius: 2,
                                        borderLeft: '3px solid', borderLeftColor: 'secondary.main',
                                        transition: 'box-shadow 0.2s, border-color 0.2s',
                                        '&:hover': { boxShadow: theme.shadows[3], borderColor: 'secondary.main' },
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap', mb: 0.75 }}>
                                            <Chip label={getEventTypeLabel(log.eventType)} size="small" sx={{
                                                bgcolor: `${chipColor}18`, color: chipColor, fontWeight: 600,
                                                fontSize: '0.72rem', height: 'auto', '& .MuiChip-label': { px: 1, whiteSpace: 'normal', lineHeight: 1.4 },
                                                py: 0.25,
                                            }} />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    ml: { xs: 0, sm: 'auto' },
                                                    whiteSpace: 'nowrap',
                                                    fontWeight: 600,
                                                    fontSize: '0.875rem',
                                                    color: timelineAccentColor,
                                                }}
                                            >
                                                {date} &middot; {time}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" sx={{ color: 'text.primary', mb: 0.5, wordBreak: 'break-word' }}>
                                            {log.eventDetails || 'No description available.'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>by {log.loggedUser}</Typography>
                                    </Paper>
                                </Box>
                            );
                        })}

                        {/* Load More */}
                        {hasMore && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <Button variant="outlined" color="secondary" size="small" onClick={handleLoadMore} disabled={loadingMore}
                                    sx={{ textTransform: 'none', fontWeight: 600, px: 3 }}
                                >
                                    {loadingMore ? (
                                        <><CircularProgress size={16} color="secondary" sx={{ mr: 1 }} /> Loading...</>
                                    ) : (
                                        `Load more (${logs.length} of ${total})`
                                    )}
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    );
};
